import React, { useEffect, useMemo, useState } from "react";
import { X, Save, Settings2, AlertCircle, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import LoadingSpinner from "@/components/ui/LoadingSpinner";

// ---------- types ----------

interface ParamVariable {
  id: string;
  label: string;
  description: string;
  example: string;
  category: "student" | "context" | "counselor" | "campaign" | "system" | "ai";
}

interface ParamHint {
  index: number;
  variableId?: string;
  fallback?: string;
  label?: string;
  hint?: string;
}

interface Template {
  id: string;
  name: string;
  text?: string;
  sampleText?: string;
  totalParameters: number;
  paramHints?: ParamHint[];
}

interface Props {
  template: Template;
  onClose: () => void;
  onSaved?: (updated: Template) => void;
}

const AI_CHOICE = "ai.choice";

// ---------- component ----------

const TemplateHintsEditor: React.FC<Props> = ({ template, onClose, onSaved }) => {
  const [variables, setVariables] = useState<ParamVariable[]>([]);
  const [loadingVars, setLoadingVars] = useState(true);
  const [hints, setHints] = useState<ParamHint[]>(() => buildInitial(template));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingVars(true);
      try {
        const resp = await api.get<ParamVariable[]>(
          "/admin/whatsapp/param-variables",
        );
        const list = Array.isArray(resp) ? resp : (resp as any)?.data ?? [];
        if (!cancelled) setVariables(list);
      } catch (err: any) {
        if (!cancelled) setError(err?.message || "Failed to load variables");
      } finally {
        if (!cancelled) setLoadingVars(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  const variablesById = useMemo(() => {
    const m = new Map<string, ParamVariable>();
    variables.forEach((v) => m.set(v.id, v));
    return m;
  }, [variables]);

  // Group variables by category for nicer dropdown
  const byCategory = useMemo(() => {
    const groups: Record<string, ParamVariable[]> = {};
    for (const v of variables) {
      (groups[v.category] = groups[v.category] || []).push(v);
    }
    return groups;
  }, [variables]);

  const categoryOrder: Array<keyof typeof byCategory> = [
    "student",
    "context",
    "counselor",
    "campaign",
    "system",
    "ai",
  ];

  const updateHint = (index: number, patch: Partial<ParamHint>) => {
    setHints((prev) =>
      prev.map((h) => (h.index === index ? { ...h, ...patch } : h)),
    );
  };

  const renderedPreview = useMemo(() => {
    if (!template.text) return "";
    return template.text.replace(/\{\{\s*(\d+)\s*\}\}/g, (_, n) => {
      const idx = Number(n);
      const hint = hints.find((h) => h.index === idx);
      if (!hint?.variableId) return `{{${n}}}`;
      if (hint.variableId === AI_CHOICE) {
        return `[AI: ${hint.label || "tailored text"}]`;
      }
      const v = variablesById.get(hint.variableId);
      return v?.example || hint.fallback || `{{${n}}}`;
    });
  }, [template.text, hints, variablesById]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      // Drop empty entries (no variableId set)
      const payload = hints
        .filter((h) => !!h.variableId || h.label || h.hint || h.fallback)
        .map((h) => ({
          index: h.index,
          variableId: h.variableId || undefined,
          fallback: h.fallback || undefined,
          label: h.label || undefined,
          hint: h.hint || undefined,
        }));
      const updated = await api.patch<Template>(
        `/admin/whatsapp/templates/${template.id}/hints`,
        { paramHints: payload },
      );
      onSaved?.(updated as any);
      onClose();
    } catch (err: any) {
      setError(err?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-orange-100 flex items-center justify-center">
              <Settings2 className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <h2 className="font-semibold text-gray-900">
                Map template parameters
              </h2>
              <p className="text-xs text-gray-500">
                Template: <span className="font-mono">{template.name}</span> ·{" "}
                {template.totalParameters} placeholder
                {template.totalParameters === 1 ? "" : "s"}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {loadingVars ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner size="md" />
            </div>
          ) : template.totalParameters === 0 ? (
            <div className="px-5 py-12 text-center text-sm text-gray-500">
              This template has no <code className="text-xs">{"{{n}}"}</code>{" "}
              placeholders — nothing to map.
            </div>
          ) : (
            <>
              {/* Template body */}
              {template.text && (
                <div className="px-5 py-3 bg-gray-50 border-b border-gray-200">
                  <p className="text-xs font-medium text-gray-500 mb-1">
                    Template body
                  </p>
                  <p className="text-sm text-gray-800 whitespace-pre-wrap">
                    {template.text}
                  </p>
                </div>
              )}

              {/* Per-{{n}} editor */}
              <div className="divide-y divide-gray-100">
                {hints.map((hint) => {
                  const placeholder = `{{${hint.index}}}`;
                  const selectedVar = hint.variableId
                    ? variablesById.get(hint.variableId)
                    : undefined;
                  const isAi = hint.variableId === AI_CHOICE;
                  return (
                    <div key={hint.index} className="px-5 py-4 grid grid-cols-1 md:grid-cols-12 gap-3">
                      <div className="md:col-span-1">
                        <span className="text-sm font-mono font-semibold text-orange-600">
                          {placeholder}
                        </span>
                      </div>

                      <div className="md:col-span-4">
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Variable
                        </label>
                        <select
                          value={hint.variableId || ""}
                          onChange={(e) =>
                            updateHint(hint.index, {
                              variableId: e.target.value || undefined,
                            })
                          }
                          className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                        >
                          <option value="">— Pick a variable —</option>
                          {categoryOrder.map((cat) => {
                            const list = byCategory[cat];
                            if (!list?.length) return null;
                            return (
                              <optgroup
                                key={cat}
                                label={categoryLabel(cat as string)}
                              >
                                {list.map((v) => (
                                  <option key={v.id} value={v.id}>
                                    {v.label}
                                  </option>
                                ))}
                              </optgroup>
                            );
                          })}
                        </select>
                        {selectedVar && (
                          <p className="text-[11px] text-gray-500 mt-1 leading-tight">
                            {selectedVar.description}
                          </p>
                        )}
                      </div>

                      <div className="md:col-span-3">
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          {isAi ? "AI prompt hint" : "Fallback (if empty)"}
                        </label>
                        {isAi ? (
                          <input
                            type="text"
                            value={hint.hint || ""}
                            onChange={(e) =>
                              updateHint(hint.index, { hint: e.target.value })
                            }
                            placeholder="e.g. one short action specific to this student"
                            className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-orange-400"
                          />
                        ) : (
                          <input
                            type="text"
                            value={hint.fallback || ""}
                            onChange={(e) =>
                              updateHint(hint.index, { fallback: e.target.value })
                            }
                            placeholder="e.g. there"
                            className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-orange-400"
                          />
                        )}
                        {isAi && (
                          <input
                            type="text"
                            value={hint.label || ""}
                            onChange={(e) =>
                              updateHint(hint.index, { label: e.target.value })
                            }
                            placeholder="Short slot name (admin-only)"
                            className="w-full rounded-md border border-gray-300 px-2 py-1 text-xs mt-1 focus:outline-none focus:ring-1 focus:ring-orange-400"
                          />
                        )}
                      </div>

                      <div className="md:col-span-4">
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Preview value
                        </label>
                        <div
                          className={cn(
                            "min-h-[34px] rounded-md border px-2 py-1.5 text-xs",
                            isAi
                              ? "bg-blue-50 border-blue-200 text-blue-800"
                              : selectedVar
                                ? "bg-green-50 border-green-200 text-green-900"
                                : "bg-gray-50 border-gray-200 text-gray-400",
                          )}
                        >
                          {isAi ? (
                            <span className="inline-flex items-center gap-1">
                              <Sparkles className="h-3 w-3" />
                              AI-generated at send-time
                            </span>
                          ) : selectedVar ? (
                            selectedVar.example
                          ) : hint.fallback ? (
                            <span className="italic">fallback: {hint.fallback}</span>
                          ) : (
                            <span className="italic">— pick a variable —</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Rendered preview */}
              <div className="px-5 py-4 border-t border-gray-200 bg-orange-50/30">
                <p className="text-xs font-medium text-orange-800 mb-1">
                  Rendered preview (example values)
                </p>
                <div className="rounded-md border border-orange-200 bg-white px-3 py-2 text-sm whitespace-pre-wrap text-gray-800">
                  {renderedPreview}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="mx-5 my-3 bg-red-50 border border-red-200 rounded-md p-3 flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-red-600 mt-0.5" />
            <p className="text-xs text-red-700">{error}</p>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || template.totalParameters === 0}
            className={cn(
              "inline-flex items-center gap-2 px-4 py-2 rounded-md text-white text-sm font-medium transition-colors",
              !saving && template.totalParameters > 0
                ? "bg-orange-500 hover:bg-orange-600"
                : "bg-gray-300 cursor-not-allowed",
            )}
          >
            {saving ? <LoadingSpinner size="sm" /> : <Save className="h-4 w-4" />}
            {saving ? "Saving..." : "Save mappings"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------- helpers ----------

function buildInitial(template: Template): ParamHint[] {
  const existing = new Map<number, ParamHint>();
  (template.paramHints || []).forEach((h) => {
    if (typeof h?.index === "number") existing.set(h.index, h);
  });
  const out: ParamHint[] = [];
  for (let i = 1; i <= (template.totalParameters || 0); i++) {
    out.push(
      existing.get(i) || {
        index: i,
      },
    );
  }
  return out;
}

function categoryLabel(c: string): string {
  return (
    {
      student: "Student profile",
      context: "Conversation context",
      counselor: "Counselor",
      campaign: "Campaign history",
      system: "System / dynamic",
      ai: "AI freeform",
    }[c] || c
  );
}

export default TemplateHintsEditor;
